import { createContext, useContext, useEffect, useState, useCallback } from "react";
import API from "../api/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const navigate = useNavigate();
  const [cartId, setCartId] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // ================= CREATE NEW CART =================
  const createNewCart = useCallback(async () => {
  const token = localStorage.getItem("access");
  if (!token) {
    // Show popup instead of console.warn
    await Swal.fire({
      icon: "info",
      title: "Login Required",
      text: " Please login to order.",
      confirmButtonText: "Login",
      showCloseButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/login"); // redirect to login page
      }
    });

    return null;
  }

    try {
      const res = await API.post(
        "cart/create/",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newId = res.data.cart_id || res.data.id;
      if (!newId) return null;

      localStorage.setItem("cart_id", newId);
      setCartId(newId);
      setCartCount(0);
      return newId;
    } catch (err) {
      console.error("Create cart error:", err);
      return null;
    }
  }, []);

  // ================= FETCH CART COUNT =================
  const fetchCartCount = useCallback(
    async (idParam = null) => {
      const token = localStorage.getItem("access");
      if (!token) return; // guest: skip fetching cart

      try {
        const id = idParam || cartId;
        if (!id) return;

        const res = await API.get(`cart/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = res.data?.items || [];
        const count = items.reduce((sum, item) => sum + Number(item.quantity), 0);
        setCartCount(count);
      } catch (err) {
        if (err.response?.status === 404) {
          localStorage.removeItem("cart_id");
          const newId = await createNewCart();
          if (newId) setCartCount(0);
        } else {
          console.error("Fetch cart count error:", err);
        }
      }
    },
    [cartId, createNewCart]
  );

  // ================= UPDATE CART COUNT INSTANTLY =================
  const updateCartCountInstant = (delta) => {
    setCartCount((prev) => Math.max(prev + delta, 0));
  };

  // ================= INIT CART ON LOAD =================
  useEffect(() => {
    const initCart = async () => {
      const token = localStorage.getItem("access");
      if (!token) {
        // guest user: no cart
        setCartId(null);
        setCartCount(0);
        setLoading(false);
        return;
      }

      let stored = localStorage.getItem("cart_id");
      if (!stored || stored === "undefined" || stored === "null") {
        stored = await createNewCart();
      } else {
        setCartId(stored);
      }
      if (stored) await fetchCartCount(stored);
      setLoading(false);
    };
    initCart();
  }, [createNewCart, fetchCartCount]);

  // ================= ADD TO CART =================
  const addToCart = async (productId, quantity = 1, color = "", size = "") => {
    const token = localStorage.getItem("access");
    if (!token) {
      alert("Please login to add products to cart.");
      navigate("/login");
      return;
    }

    try {
      let id = cartId;
      if (!id) id = await createNewCart();
      if (!id) return;

      await API.post(
        "/cart/add/",
        { cart_id: id, product_id: productId, quantity, color, size },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCartCountInstant(quantity);
      fetchCartCount(id);
    } catch (err) {
      console.error("Add to cart error:", err.response?.data || err.message);
    }
  };

  // ================= UPDATE CART ITEM =================
  const updateCartItem = async (itemId, newQty, oldQty) => {
    const token = localStorage.getItem("access");
    if (!token) return;

    try {
      await API.post(
        "/cart/item/update/",
        { item_id: itemId, quantity: newQty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCartCountInstant(newQty - oldQty);
    } catch (err) {
      console.error("Update cart item error:", err);
      throw err;
    }
  };

  // ================= REMOVE CART ITEM =================
  const removeCartItem = async (itemId, qty) => {
    const token = localStorage.getItem("access");
    if (!token) return;

    try {
      await API.post(
        "/cart/item/remove/",
        { item_id: itemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCartCountInstant(-qty);
    } catch (err) {
      console.error("Remove cart item error:", err);
      throw err;
    }
  };

  // ================= CLEAR CART =================
  const clearCart = useCallback(async () => {
    const token = localStorage.getItem("access");
    if (!token) return;

    try {
      if (cartId) {
        const res = await API.get(`/cart/${cartId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = res.data?.items || [];
        for (let item of items) {
          await API.post(
            "/cart/item/remove/",
            { item_id: item.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
    } catch (err) {
      console.error("Error clearing cart:", err);
    } finally {
      localStorage.removeItem("cart_id");
      setCartId(null);
      setCartCount(0);
      await createNewCart();
    }
  }, [cartId, createNewCart]);

  const value = {
    cartId,
    cartCount,
    loading,
    addToCart,
    updateCartItem,
    removeCartItem,
    fetchCartCount,
    updateCartCountInstant,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};